import FormShell from "@/components/form-shell";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useRightPanel } from "@/hooks/useRightPanel";
import { authClient } from "@/lib/auth-client";
import { loginSchema, type LoginFormValues } from "@/schemas/login-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const { setRight } = useRightPanel();

  useEffect(() => {
    setRight(<div className="flex h-full flex-1 items-center justify-center overflow-hidden p-6 text-6xl font-bold text-black">Nautika</div>);
  }, [setRight]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: session, error, isPending } = authClient.useSession();

  // Redirige si déjà connecté (seulement si on n'est pas en cours de chargement de connexion)
  useEffect(() => {
    if (isPending || isLoading) return; // Ne pas rediriger si la session est en cours de chargement OU si on est en train de se connecter
    if (error) {
      console.error("Erreur de session:", error);
      return;
    }
    if (session?.user) {
      if (session.user.onboarding === "done") {
        // Si l'onboarding est terminé, rediriger vers le dashboard
        // La vérification de l'abonnement sera faite par ProtectedRoute
        navigate("/dash", { replace: true });
      } else {
        navigate(`/onboarding/${session.user.onboarding}`, {
          replace: true,
        });
      }
    }
  }, [session, navigate, error, isPending, isLoading]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    defaultValues: {
      email: searchParams.get("email") || "",
      password: "",
    },
  });
  // Watch email field to update signup link dynamically (useWatch is React Compiler friendly)
  const emailValue = useWatch({ control: form.control, name: "email" });

  // Focus sur le champ mot de passe si l'email est pré-rempli
  useEffect(() => {
    if (searchParams.get("email")) {
      form.setFocus("password");
    } else {
      form.setFocus("email");
    }

    if (searchParams.get("error") === "INVALID_TOKEN_RESET_PASSWORD") {
      toast.error("Lien de réinitialisation invalide ou expiré, veuillez réessayer.", {
        action: {
          label: "Réessayer",
          onClick: () => {
            navigate("/forget-password", { replace: true });
          },
        },
        id: "invalid-token-reset-password",
      });
      // supprimer le paramètre de l'URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("error");
      navigate(`/login?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate, form]);

  const onSubmit = (data: LoginFormValues) => {
    console.log("Tentative de connexion...");
    setIsLoading(true);
    authClient.signIn.email(
      { email: data.email, password: data.password },
      {
        onSuccess: (context) => {
          // Récupérer les informations de session depuis le contexte
          const userSession = context.data;

          // Petit délai pour garder le loader visible et donner une sensation de fluidité
          setTimeout(() => {
            // Redirige vers la page d'onboarding ou le tableau de bord
            // Ne pas remettre setIsLoading(false) ici pour garder le loader jusqu'à la navigation
            if (userSession?.user?.onboarding === "done") {
              // Si l'onboarding est terminé, rediriger vers le dashboard
              // La vérification de l'abonnement sera faite par ProtectedRoute
              navigate("/dash", { replace: true });
            } else if (userSession?.user?.onboarding) {
              navigate(`/onboarding/${userSession.user.onboarding}`, {
                replace: true,
              });
            } else {
              // Fallback si pas d'informations d'onboarding
              navigate("/dash", { replace: true });
            }
          }, 500); // Délai de 500ms pour garder le loader visible
        },
        onError: (error) => {
          const authError = error as { error?: { code?: string; message?: string } };
          if (authError.error?.code === "INVALID_EMAIL_OR_PASSWORD") {
            form.setError("password", {
              type: "manual",
              message: "Email ou mot de passe incorrect",
            });
          } else if (authError.error?.code === "EMAIL_NOT_VERIFIED") {
            console.log("Email not verified");
            navigate("/onboarding/step2", {
              replace: true,
            });
          } else {
            console.error("Erreur connexion:", authError);
            form.setError("email", {
              type: "manual",
              message: "Une erreur est survenue. Veuillez réessayer.",
            });
          }
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <FormShell
      title="Connectez-vous à votre compte"
      subtitle="Entrez votre email ci-dessous pour vous connecter"
      footer={
        <div>
          <Link to={`/onboarding${emailValue ? `?email=${encodeURIComponent(emailValue)}` : ""}`} className="text-sm underline-offset-4 hover:underline">
            Vous n&apos;avez pas de compte ? <span className="underline underline-offset-4">Inscrivez-vous</span>
          </Link>
        </div>
      }
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Form {...form}>
        <div className="grid gap-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="grid gap-3">
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="mail@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="grid gap-3">
                <div className="flex items-center">
                  <FormLabel>Mot de passe</FormLabel>
                  <Link
                    to="#"
                    className="ml-auto text-sm underline-offset-4 hover:underline"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.preventDefault();
                      const email = form.getValues("email");
                      navigate(`/forget-password${email ? `?email=${encodeURIComponent(email)}` : ""}`);
                    }}
                  >
                    Mot de passe oublié?
                  </Link>
                </div>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Connexion
          </Button>
        </div>
      </Form>
    </FormShell>
  );
}
